import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAudioAPI } from '../hooks/useAudioAPI';
import { useWavesurfer } from '@wavesurfer/react';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import { ChevronDown, ChevronUp, MessageCircle, Play, Pause, Upload, Trash2, LogOut, ZoomIn, ZoomOut, Share2, Copy, Check, X, FileText, Loader2, Download, Lock, Link2 } from 'lucide-react';
import FileStatusBadge from '../components/FileStatusBadge';
import WaveformSkeleton from '../components/WaveformSkeleton';
import KaraokeView from '../components/KaraokeView';
import UploadModal from '../components/UploadModal';
import LinkClientModal from '../components/LinkClientModal';
import { BACKEND_URL } from '../lib/backend';

// Constants
const MAX_ZOOM = 10;
const MIN_ZOOM = 1;

// Share link lifetimes — keys must match SHARE_TTL_PRESETS in backend/routes/share.py
const SHARE_TTL_OPTIONS = [
  { key: '6h', label: '6 часов' },
  { key: '1d', label: '1 день' },
  { key: '3d', label: '3 дня' },
  { key: '1w', label: '1 неделя' },
  { key: '1m', label: '1 месяц' },
];
const SHARE_TTL_DEFAULT = '1d';

// Memoized comment item component to avoid unnecessary re-renders
const CommentItem = React.memo(({ comment, marker, getInitials, getAvatarColor, formatTime, seekToTime, deleteComment, currentUserId, isAdmin }) => {
  const canDelete = isAdmin || comment.user_id === currentUserId;
  return (
    <div
      className="flex items-start space-x-3 p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors group cursor-pointer"
      onClick={() => seekToTime(marker.timestamp, true)}
      data-testid={`comment-${comment.id}`}
    >
      <div
        className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(comment.user_name)} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}
      >
        {getInitials(comment.user_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2 mb-1">
          <span className="font-medium text-sm text-white">{comment.user_name}</span>
          <span className="text-xs text-orange-500">
            @ {formatTime(marker.timestamp)}
          </span>
        </div>
        <p className="text-sm text-gray-300 break-words">{comment.text}</p>
      </div>
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteComment(comment.id, marker.id);
          }}
          className="text-gray-500 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
          data-testid={`delete-comment-${comment.id}`}
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
});

const MainPage = () => {
  const { user, logout, getAuthHeaders, token, isGuest, guestSharedToken, guestSharedFile, loginUser } = useAuth();
  // Пришли по прямой ссылке /r/<id> — открываем сверху именно эту запись.
  const { fileId: requestedFileId } = useParams();

  // Stable getter for token — avoids recreating useAudioAPI callbacks on every token change
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const getToken = useCallback(() => tokenRef.current, []);

  const guestSharedTokenRef = useRef(guestSharedToken);
  guestSharedTokenRef.current = guestSharedToken;
  const getShareToken = useCallback(() => guestSharedTokenRef.current, []);

  const {
    files,
    markers,
    comments,
    fetchFiles,
    uploadFile,
    uploadDualFile,
    downloadFile,
    fetchFileDetails,
    deleteFile,
    updateFileTitle,
    updateFileAccess,
    searchClients,
    fetchClientSessions,
    startTranscription,
    fetchTranscript,
    updateSpeakerLabels,
    fetchMarkersForAudio,
    createMarkerWithComment,
    deleteComment,
    addOptimisticFile,
    removeOptimisticFile,
    patchFileInList,
    resetMarkersAndComments
  } = useAudioAPI(getAuthHeaders, getToken, getShareToken);

  const isAdmin = user?.role === 'admin';

  const [currentFile, setCurrentFile] = useState(null);
  // CONCURRENT uploads: each in-flight upload is keyed by its optimistic
  // tempId → { progress: 0..100|null, stage: string|null }. A key's presence
  // means that upload is in flight; its absence means done/failed. This map
  // replaces the old single-valued (uploading / uploadProgress /
  // uploadingTempId / uploadStage) state, so MULTIPLE files can upload at once,
  // each rendering its own progress row. progress: % of bytes sent (0..100);
  // once at 100 the `stage` mirrors Mongo processing_stage ('queued' |
  // 'compressing' | 'generating_peaks' | 'uploading_to_s3' / 'mixing') for the
  // server-side compress/peaks/S3 phase.
  const [uploads, setUploads] = useState({});
  const uploadingCount = Object.keys(uploads).length;
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  // Строка, по которой только что кликнули без доступа: под ней показывается
  // короткое объяснение, почему запись не открывается.
  const [lockedHintId, setLockedHintId] = useState(null);
  // Окно привязки записи к человеку из гипнозио (только у админа)
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkNotice, setLinkNotice] = useState('');
  // Пришли по прямой ссылке /r/<id>, а открыть нечего: 'locked' — запись есть,
  // но не наша; 'missing' — такой записи нет вовсе.
  const [requestedProblem, setRequestedProblem] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [localFiles, setLocalFiles] = useState({}); // Store local file references for instant playback
  // waveformCache persisted to localStorage for instant rendering across sessions
  const [waveformCache, setWaveformCacheState] = useState(() => {
    try {
      const saved = localStorage.getItem('waveformCache_v4');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [isLoadingWaveform, setIsLoadingWaveform] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [playbackRate, setPlaybackRate] = useState(1); // Playback speed: 1, 1.5, 2, 2.5, 3
  const [shareModalUrl, setShareModalUrl] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  // Share link lifetime: token stays the same when the user switches presets,
  // only `expires_at` is re-stamped server-side (see PATCH /api/share/{token}).
  const [shareToken, setShareToken] = useState(null);
  const [shareTtl, setShareTtl] = useState(SHARE_TTL_DEFAULT);
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [shareTtlPending, setShareTtlPending] = useState(false);
  const [guestLoginModalOpen, setGuestLoginModalOpen] = useState(false);
  const [guestLoginForm, setGuestLoginForm] = useState({ login: '', password: '' });
  const [guestLoginError, setGuestLoginError] = useState('');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [transcriptCopied, setTranscriptCopied] = useState(false);
  // 'karaoke' (original word-by-word) | 'translation' (Russian sentence-by-sentence) | 'text' (plain)
  // Default is overridden to 'translation' for foreign-language files inside
  // the panel render — see hasTranslation below.
  const [transcriptViewMode, setTranscriptViewMode] = useState('karaoke');
  const [filesLoading, setFilesLoading] = useState(true);

  const containerRef = useRef(null);
  const waveformWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentFileRef = useRef(null);
  const loadingFileRef = useRef(null); // Track which file we're waiting for

  // Custom waveformCache setter that also persists to localStorage
  const setWaveformCache = useCallback((updater) => {
    setWaveformCacheState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('waveformCache_v4', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  currentFileRef.current = currentFile;

  // Get cached peaks for current file.
  // Normalise to WaveSurfer v7 format: number[][] (array of channels).
  // Server stores a flat number[], localStorage stores exportPeaks() result which is already number[][].
  const rawCachedPeaks = currentFile?.id ? waveformCache[currentFile.id] : null;
  const cachedPeaks = rawCachedPeaks
    ? (Array.isArray(rawCachedPeaks[0]) ? rawCachedPeaks : [rawCachedPeaks])
    : null;

  // Peaks from the backend are already RMS-normalized (p99) and ready to
  // render — no frontend transform needed.
  const displayPeaks = useMemo(() => cachedPeaks, [cachedPeaks]);

  // Dual-track rendering: pass two channels to ONE canvas (no splitChannels).
  // WaveSurfer's line renderer naturally draws channel 0 upward and channel 1
  // downward from centerline (see calculateLinePaths: direction = index===0
  // ? -1 : 1) — exactly the "upper = expert, lower = client" split the user
  // wants, as a single smooth polygon identical in style to the mono render.
  //
  // Why not barAlign + splitChannels? That path switches the renderer to
  // bar-mode (utils.shouldRenderBars returns true whenever barAlign is set),
  // which produces flat-topped rectangles at max amplitude — visibly
  // different from the smooth line render mono uses.
  //
  // Colors use wavesurfer's vertical gradient (color passed as array). Top
  // stop drives expert half, bottom stop drives client half.
  //   unplayed: dark muted tints, pushed toward dark-neutral so the saturated
  //             played colors really "pop" visually (prev pale #ad7f66 /
  //             #6696ad was too close in luminance to played, made the
  //             progress indicator nearly invisible).
  //   played:   mono's #ff5500 (expert, matches mono orange exactly) and a
  //             brighter cyan #00b4ff (client) — both saturated.
  const isDualTrack = !!currentFile?.dual_track && Array.isArray(displayPeaks) && displayPeaks.length >= 2;
  const dualWaveColor = useMemo(() => ['#6e4f3c', '#3c4f6e'], []);
  const dualProgressColor = useMemo(() => ['#ff5500', '#00b4ff'], []);

  // Stable plugin list — must not recreate on each render to avoid WaveSurfer destroy/init loop
  const wsPlugins = useMemo(() => [
    TimelinePlugin.create({
      height: 20,
      style: {
        color: '#6b7280',
        backgroundColor: '#111827',
        fontSize: '10px',
      },
      formatTimeCallback: (s) => {
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
      },
    }),
  ], []);

  const { wavesurfer, isReady, isPlaying, currentTime } = useWavesurfer({
    container: containerRef,
    url: audioUrl,
    waveColor: isDualTrack ? dualWaveColor : '#8a8a8a',
    progressColor: isDualTrack ? dualProgressColor : '#ff5500',
    cursorColor: '#ff5500',
    cursorWidth: 2,
    height: 128,
    normalize: true,
    minPxPerSec: 1,
    peaks: displayPeaks,
    plugins: wsPlugins,
  });


  // Use audioDuration state instead of useMemo for proper reactivity
  // Fall back to file duration if wavesurfer hasn't loaded yet
  const duration = audioDuration > 0 ? audioDuration : (currentFile?.duration || 0);

  // Update duration when wavesurfer is ready
  useEffect(() => {
    if (wavesurfer && isReady && audioUrl) {
      try {
        console.log('🎵 Wavesurfer готов к воспроизведению');
        const newDuration = wavesurfer.getDuration();
        if (newDuration && newDuration > 0 && !isNaN(newDuration)) {
          console.log(`⏱️  Длительность трека: ${Math.floor(newDuration / 60)}:${Math.floor(newDuration % 60).toString().padStart(2, '0')} (${newDuration.toFixed(2)}s)`);
          setAudioDuration(newDuration);
        }
      } catch (e) {
        console.error('❌ Ошибка получения длительности:', e);
      }
    }
  }, [wavesurfer, isReady, audioUrl]);

  // Update zoom when duration or zoom changes
  useEffect(() => {
    // CRITICAL: Only apply zoom when everything is ready
    if (!wavesurfer || !isReady || !audioUrl) {
      return;
    }
    
    // Double-check wavesurfer has audio loaded
    let currentDuration = 0;
    try {
      currentDuration = wavesurfer.getDuration();
      if (!currentDuration || currentDuration === 0 || isNaN(currentDuration)) {
        console.log('⏭️  Пропуск зума: wavesurfer сообщает о неверной длительности');
        return;
      }
    } catch (e) {
      console.log('⏭️  Пропуск зума: невозможно получить длительность от wavesurfer', e);
      return;
    }
    
    // Use current duration from wavesurfer for calculation
    if (!containerRef.current) {
      return;
    }
    
    const containerWidth = containerRef.current.parentElement?.clientWidth || 800;
    const basePxPerSec = containerWidth / currentDuration;
    
    if (!basePxPerSec || basePxPerSec <= 0 || isNaN(basePxPerSec)) {
      console.log('⏭️  Пропуск зума: неверный basePxPerSec');
      return;
    }
    
    try {
      // Exponential zoom: zoom=1 → fits container, zoom=10 → 100px/sec
      // Exponential scale gives smooth visual change at all zoom levels
      // (linear + Math.max caused dead zones for long files)
      const MAX_ZOOM_PX_PER_SEC = 100;
      const t = (zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM); // 0..1
      const targetMax = Math.max(MAX_ZOOM_PX_PER_SEC, basePxPerSec);
      const zoomedValue = basePxPerSec * Math.pow(targetMax / basePxPerSec, t);
      
      console.log(`🔍 Применение зума: длительность=${currentDuration.toFixed(2)}s, basePxPerSec=${basePxPerSec.toFixed(2)}, zoom=x${zoom}, итоговый minPxPerSec=${zoomedValue.toFixed(2)}`);
      wavesurfer.zoom(zoomedValue);
    } catch (error) {
      console.error('❌ Ошибка применения зума:', error);
    }
  }, [wavesurfer, isReady, zoom, audioUrl]);

  // Cache waveform peaks after first load for instant rendering
  useEffect(() => {
    if (wavesurfer && isReady && currentFile?.id) {
      // Check if already cached
      if (!waveformCache[currentFile.id]) {
        try {
          const peaks = wavesurfer.exportPeaks({ maxLength: 10000 });
          if (peaks && peaks.length > 0) {
            console.log(`💾 Кэширование пиков вейвформа для файла ${currentFile.id} (${peaks.length} точек данных)`);
            setWaveformCache(prev => ({
              ...prev,
              [currentFile.id]: peaks
            }));
          }
        } catch (error) {
          console.error('❌ Ошибка кэширования пиков:', error);
        }
      }
      // Hide loading state only when it's the file we're waiting for
      if (currentFile.id === loadingFileRef.current) {
        console.log('✅ Скрытие индикатора загрузки - вейвформ готов');
        setIsLoadingWaveform(false);
        loadingFileRef.current = null;
      }
    }
  }, [wavesurfer, isReady, currentFile, waveformCache, setWaveformCache]);

  // Fetch markers when the waveform is ready. Both /api/markers/{id} and
  // /api/comments/by-file/{id} now accept ?share_token= alongside the usual
  // JWT, so guests viewing a track via a share link see the existing
  // comments — useAudioAPI auto-appends the token when no JWT is present.
  useEffect(() => {
    if (isReady && currentFileRef.current) {
      console.log(`📝 Загрузка комментариев для файла ${currentFileRef.current.id}...`);
      fetchMarkersForAudio(currentFileRef.current.id);
    }
  }, [isReady, fetchMarkersForAudio]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Fetch files on mount (skip for guests — they only see one shared file)
  useEffect(() => {
    if (isGuest) { setFilesLoading(false); return; }
    setFilesLoading(true);
    fetchFiles().finally(() => setFilesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Multi-file transcription poller. The backend already runs transcription
  // jobs in parallel (fire-and-forget create_task); this lets the UI TRACK
  // several at once. We derive the set of in-flight file ids from the files
  // list (any pending/running), and poll ALL of them on one shared 5s interval
  // — so a job started on file A keeps updating its row badge even after you
  // switch to file B. For whichever file is currently open, we also merge the
  // full transcript payload so the open panel updates live.
  //
  // The effect re-subscribes only when the SET of in-flight ids changes (keyed
  // on inFlightTranscriptionKey), not on every field patch — otherwise patching
  // a status each tick would tear down and rebuild the interval every 5s.
  const inFlightTranscriptionIds = useMemo(
    () => files
      .filter(f => f.transcript_status === 'pending' || f.transcript_status === 'running')
      .map(f => f.id),
    [files]
  );
  const inFlightTranscriptionKey = useMemo(
    () => [...inFlightTranscriptionIds].sort().join(','),
    [inFlightTranscriptionIds]
  );

  useEffect(() => {
    // fetchTranscript hits an admin-only endpoint (GET /files/{id}/transcript,
    // Depends(require_admin)). The files list is global and exposes
    // transcript_status to every authenticated user, so without this guard a
    // non-admin would poll any admin-in-progress file every 5s → silent 403
    // forever (the id never resolves to ready/error from their view). The UI
    // only renders running/error transcript states for admins anyway.
    if (!isAdmin || !inFlightTranscriptionKey) return;
    const ids = inFlightTranscriptionKey.split(',');
    let cancelled = false;
    const tick = async () => {
      await Promise.all(ids.map(async (id) => {
        try {
          const data = await fetchTranscript(id);
          if (cancelled || !data) return;
          // Update the list row (badge/status) for every tracked file.
          patchFileInList(id, {
            transcript_status: data.transcript_status,
            transcript_error: data.transcript_error,
          });
          // If this is the open file, merge the full payload so the panel
          // (text / karaoke / translation) updates live.
          setCurrentFile(prev => (prev && prev.id === id) ? { ...prev, ...data } : prev);
        } catch (err) {
          // Transient — keep polling on the next tick.
        }
      }));
    };
    const interval = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdmin, inFlightTranscriptionKey, fetchTranscript, patchFileInList]);

  // Background poller for files stuck in `status: 'processing'`. The normal
  // upload→ready transition is driven by handleFileUpload's own poll loop, but
  // that loop does NOT survive a page reload — so a file that finished
  // server-side after a reload would show a frozen "Обработка" badge until the
  // user manually refreshes. This effect polls every processing file (real
  // server rows only — temp- optimistic rows are driven by their own upload
  // loop) on a shared 4s interval and merges the fresh status into the list, so
  // badges self-heal without a reload. Only the lightweight status fields are
  // patched (not the heavy peaks payload) — the full file is pulled lazily via
  // loadAudioFile when the user opens it. The interval re-subscribes only when
  // the SET of processing ids changes (keyed on processingKey), so per-tick
  // patches don't thrash it.
  const processingIds = useMemo(
    () => files
      .filter(f => f.status === 'processing' && !String(f.id).startsWith('temp-'))
      .map(f => f.id),
    [files]
  );
  const processingKey = useMemo(
    () => [...processingIds].sort().join(','),
    [processingIds]
  );

  useEffect(() => {
    if (!processingKey) return;
    const ids = processingKey.split(',');
    let cancelled = false;
    const tick = async () => {
      await Promise.all(ids.map(async (id) => {
        try {
          const details = await fetchFileDetails(id);
          if (cancelled || !details) return;
          // Light patch — just the fields the row badge needs. The id drops out
          // of processingIds once status flips to ready/error, ending the poll.
          patchFileInList(id, {
            status: details.status,
            processing_stage: details.processing_stage,
            error_message: details.error_message,
          });
          // If the user has this file open, refresh its status too.
          setCurrentFile(prev => (prev && prev.id === id)
            ? { ...prev, status: details.status, processing_stage: details.processing_stage, error_message: details.error_message }
            : prev);
        } catch (err) {
          // Transient — keep polling on the next tick.
        }
      }));
    };
    const interval = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [processingKey, fetchFileDetails, patchFileInList]);

  const handleStartTranscription = useCallback(async () => {
    if (!currentFile?.id) return;
    const fileId = currentFile.id;
    try {
      await startTranscription(fileId);
      // Mark BOTH the open file and the list row as pending so the multi-file
      // poller picks it up immediately (it derives in-flight ids from the
      // list, not from currentFile).
      setCurrentFile(prev => prev && prev.id === fileId ? { ...prev, transcript_status: 'pending', transcript_error: null } : prev);
      patchFileInList(fileId, { transcript_status: 'pending', transcript_error: null });
      setTranscriptExpanded(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Неизвестная ошибка';
      alert(`Не удалось запустить транскрипцию: ${msg}`);
    }
  }, [currentFile?.id, startTranscription, patchFileInList]);

  // Compose the dialog-style text for dual-track transcripts — paragraphs
  // grouped by speaker with labels. For single-track we use the LLM-cleaned
  // transcript blob as before.
  const getTranscriptPlainText = useCallback(() => {
    if (!currentFile) return '';
    if (currentFile.dual_track && currentFile.transcript_paragraphs?.length > 0) {
      const labels = currentFile.transcript_speaker_labels || {};
      return currentFile.transcript_paragraphs.map((p) => {
        const name = labels[String(p.speaker)] || `Спикер ${(p.speaker ?? 0) + 1}`;
        return `${name}:\n${p.text}`;
      }).join('\n\n');
    }
    return currentFile.transcript || '';
  }, [currentFile]);

  const handleCopyTranscript = useCallback(() => {
    const txt = getTranscriptPlainText();
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    setTranscriptCopied(true);
    setTimeout(() => setTranscriptCopied(false), 1500);
  }, [getTranscriptPlainText]);

  const handleUpdateSpeakerLabels = useCallback(async (newLabels) => {
    if (!currentFile?.id) return;
    try {
      const result = await updateSpeakerLabels(currentFile.id, newLabels);
      setCurrentFile(prev => prev ? { ...prev, transcript_speaker_labels: result.transcript_speaker_labels } : prev);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Не удалось обновить метки';
      alert(msg);
    }
  }, [currentFile?.id, updateSpeakerLabels]);

  const handleSeekFromKaraoke = useCallback((time) => {
    if (wavesurfer && typeof time === 'number') {
      try { wavesurfer.setTime(time); } catch (e) { console.error('seek error:', e); }
    }
  }, [wavesurfer]);

  const handleDownloadTranscript = useCallback(() => {
    const txt = getTranscriptPlainText();
    if (!txt) return;
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = (currentFile?.title || currentFile?.original_filename || 'transcript').replace(/\.mp3$/i, '');
    a.download = `${base}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [getTranscriptPlainText, currentFile?.title, currentFile?.original_filename]);

  // Какую запись открыть сверху.
  //
  // Пришли по прямой ссылке /r/<id> (её дают клиенту в кабинете) — открываем
  // именно её. Иначе первую ДОСТУПНУЮ: у студента наверху списка вполне может
  // лежать чужая проработка, а её открывать нечем.
  useEffect(() => {
    if (isGuest || files.length === 0 || currentFile) return;

    if (requestedFileId) {
      const requested = files.find((f) => f.id === requestedFileId);
      if (!requested) {
        // Записи нет в списке вовсе: удалена или ссылка неверная.
        setRequestedProblem('missing');
        return;
      }
      if (requested.locked) {
        setRequestedProblem('locked');
        return;
      }
      loadAudioFile(requested);
      return;
    }

    const firstOpen = files.find((f) => !f.locked);
    if (firstOpen) loadAudioFile(firstOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, requestedFileId]);

  // Guest: pre-load the shared file
  useEffect(() => {
    if (isGuest && guestSharedFile && !currentFile) {
      loadAudioFile(guestSharedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuest, guestSharedFile]);

  // Collapse comments when file changes
  useEffect(() => {
    setCommentsExpanded(false);
  }, [currentFile?.id]);

  // When the active file changes, pick the right default transcript view:
  //   - Foreign-language file with a stored translation → 'translation' tab
  //   - Everything else (Russian, multi, legacy without translation) → 'karaoke'
  // This is the only place the `transcriptViewMode` state is touched
  // automatically; user clicks on the toggle still win for the lifetime of
  // the file. Russian sessions never see a non-default mode here, because
  // the translation array is null for them.
  useEffect(() => {
    if (!currentFile?.id) return;
    const hasTranslation = Array.isArray(currentFile.transcript_paragraphs_ru)
      && currentFile.transcript_paragraphs_ru.length > 0;
    setTranscriptViewMode(hasTranslation ? 'translation' : 'karaoke');
  }, [currentFile?.id, currentFile?.transcript_paragraphs_ru?.length]);

  // Keyboard handler for Space to play/pause
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.code === 'Space' && wavesurfer && isReady) {
        e.preventDefault();
        wavesurfer.playPause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [wavesurfer, isReady]);

  // Zoom change handler
  const handleZoomChange = useCallback((newZoom) => {
    // Just update zoom state, useEffect will handle the application
    setZoom(newZoom);
  }, []);

  // Playback rate change handler - cycles through 1, 1.5, 2, 2.5, 3
  const handlePlaybackRateChange = useCallback(() => {
    const rates = [1, 1.5, 2, 2.5, 3];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    const newRate = rates[nextIndex];
    setPlaybackRate(newRate);
    if (wavesurfer) {
      wavesurfer.setPlaybackRate(newRate);
    }
  }, [playbackRate, wavesurfer]);

  // Load audio file
  const loadAudioFile = useCallback(async (file, localFile = null) => {
    if (!file) return;

    // Закрытая запись: сервер прислал заглушку без волны, названия и ссылки на
    // аудио — открывать нечего. Право проверяется на сервере, здесь только
    // чтобы не дёргать эндпоинты, которые заведомо ответят 403.
    if (file.locked) return;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎵 НАЧАЛО ПЕРЕКЛЮЧЕНИЯ ТРЕКА');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📂 ID файла:', file.id);
    console.log('📝 Название:', file.title || file.original_filename);
    console.log('👤 Владелец:', file.user_name);
    console.log('⏱️  Длительность:', file.duration ? `${Math.floor(file.duration / 60)}:${Math.floor(file.duration % 60).toString().padStart(2, '0')}` : 'неизвестно');

    // Track which file we're loading (for loading indicator fix)
    loadingFileRef.current = file.id;

    // Revoke previous blob URL before clearing to prevent memory leak
    if (audioUrl && audioUrl.startsWith('blob:')) {
      console.log('🗑️  Освобождение предыдущего URL объекта...');
      URL.revokeObjectURL(audioUrl);
    }

    // CRITICAL: Clear current audio and show loading state IMMEDIATELY
    console.log('🧹 Очистка текущего аудио...');
    setAudioUrl(null);
    console.log('⏳ Установка состояния загрузки...');
    setIsLoadingWaveform(true);
    setAudioDuration(0); // Reset duration for new file
    console.log('🔄 Сброс скорости воспроизведения на x1...');
    setPlaybackRate(1); // Reset playback rate to x1

    // Set current file and reset state
    console.log('💾 Установка текущего файла...');
    setCurrentFile(file);
    console.log('🗑️  Очистка маркеров и комментариев...');
    resetMarkersAndComments();
    console.log('🔍 Сброс зума на x1...');
    setZoom(1); // Always reset to zoom=1 when switching files

    // Small delay to ensure UI updates
    console.log('⏸️  Задержка для обновления UI (50ms)...');
    await new Promise(resolve => setTimeout(resolve, 50));

    try {
      let newUrl;

      // The list endpoint (/api/files) no longer returns heavy fields like
      // waveform_peaks / transcript* — they're lazy-loaded per opened track
      // via GET /api/files/{id}. Only skip the fetch if this is a fresh local
      // upload (peaks will be generated on the server after confirmation).
      let fullFile = file;
      // For dual-track files, "peaks" live in expert_peaks+client_peaks rather
      // than waveform_peaks, so the bare `!waveform_peaks` check below would
      // wrongly trigger a /api/files/{id} fetch even when peaks are already
      // present.
      const hasAnyPeaks = (file.waveform_peaks?.length > 0)
        || (file.expert_peaks?.length > 0 && file.client_peaks?.length > 0);
      const listLacksPeaks = !hasAnyPeaks && !file.id?.startsWith('temp-');
      // Guests reach the app via a share-link and have no JWT, so calling the
      // admin-protected /api/files/{id} would 500 (the auth dependency throws
      // before the handler runs). Their `guestSharedFile` already comes from
      // /api/share/{token} fully populated (peaks + transcript), so the fetch
      // is unnecessary anyway. Skip it.
      if (listLacksPeaks && !localFile && !isGuest) {
        try {
          const details = await fetchFileDetails(file.id);
          if (details) {
            // Always merge details into state when we're still on this file —
            // setCurrentFile's own callback guard (prev.id === file.id) handles
            // the "user switched tracks" race. The previous outer guard against
            // loadingFileRef.current was racy: WaveSurfer's onReady effect
            // clears that ref as soon as the waveform finishes rendering, and
            // for a second-click load (audio cached) onReady fires before the
            // ~1.5MB /files/{id} JSON arrives — dropping transcript fields and
            // making the transcript panel impossible to open until full
            // reload. Waveform-side state (fullFile / peaks) still uses the
            // ref, so cancelled loads don't repaint.
            setCurrentFile(prev => prev && prev.id === file.id ? { ...prev, ...details } : prev);
            if (loadingFileRef.current === file.id) {
              fullFile = { ...file, ...details };
            }
          }
        } catch (err) {
          console.warn('fetchFileDetails failed, will fall back to on-the-fly peaks generation:', err);
        }
      }

      // Check server peaks — dual-track uses a pair (expert_peaks + client_peaks)
      // which renders as two stacked channels; single-track uses waveform_peaks.
      const hasDualServerPeaks = fullFile.dual_track
        && fullFile.expert_peaks?.length > 0
        && fullFile.client_peaks?.length > 0;
      const hasSingleServerPeaks = !fullFile.dual_track && fullFile.waveform_peaks?.length > 0;
      const hasServerPeaks = hasDualServerPeaks || hasSingleServerPeaks;
      const hasCachedPeaks = hasServerPeaks || waveformCache[file.id];

      if (hasDualServerPeaks) {
        console.log('⚡ Двухдорожечные пики найдены - stacked render');
        setWaveformCache(prev => ({
          ...prev,
          [file.id]: [fullFile.expert_peaks, fullFile.client_peaks],
        }));
      } else if (hasSingleServerPeaks) {
        console.log('⚡ Найдены серверные пики вейвформа - моментальная отрисовка!');
        const flat = fullFile.waveform_peaks;
        const MAX = 10000;
        const downsampled = flat.length > MAX
          ? Array.from({ length: MAX }, (_, i) => flat[Math.floor(i * flat.length / MAX)])
          : flat;
        setWaveformCache(prev => ({ ...prev, [file.id]: [downsampled] }));
      } else if (waveformCache[file.id]) {
        console.log('⚡ Найдены кэшированные пики вейвформа - моментальная отрисовка!');
      } else {
        console.log('📊 Кэш вейвформа отсутствует - будет построен после загрузки');
      }

      // OPTIMIZATION: Use local file if available (just uploaded)
      if (localFile) {
        console.log('💾 Используется локальный файл (только что загружен) - скачивание не требуется!');
        newUrl = URL.createObjectURL(localFile);
        // Store reference for future use
        setLocalFiles(prev => ({ ...prev, [file.id]: localFile }));
        console.log('✅ Локальный файл сохранён в кэш');
      }
      // Check if we have local file stored
      else if (localFiles[file.id]) {
        console.log('💾 Используется кэшированный локальный файл');
        newUrl = URL.createObjectURL(localFiles[file.id]);
      }
      // Use stream URL directly (WaveSurfer handles streaming natively with auth)
      else {
        console.log('⬇️  Получение URL потока с сервера...');
        const result = await downloadFile(file.id, String(fullFile.peaks_duration ?? ''));
        if (result && result.type === 'url') {
          newUrl = result.url;
          console.log('✅ Stream URL получен:', newUrl);
        } else if (result && result.type === 'blob') {
          newUrl = URL.createObjectURL(result.blob);
          console.log('✅ Blob URL создан');
        }
      }

      console.log('🔗 Установка URL аудио для wavesurfer...');
      setAudioUrl(newUrl);

      // If no cached peaks, loading state will be cleared by useEffect when isReady
      if (hasCachedPeaks) {
        // Small delay for peaks to render
        console.log('⏸️  Задержка 100ms для отрисовки кэшированных пиков...');
        setTimeout(() => {
          if (loadingFileRef.current === file.id) {
            console.log('✅ Отключение состояния загрузки');
            setIsLoadingWaveform(false);
            loadingFileRef.current = null;
          }
        }, 100);
      } else {
        console.log('⏳ Ожидание готовности wavesurfer (событие onReady)...');
      }

      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ ЗАГРУЗКА ТРЕКА ЗАВЕРШЕНА');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    } catch (error) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ОШИБКА ЗАГРУЗКИ ТРЕКА');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('Детали ошибки:', error);
      setIsLoadingWaveform(false);
      loadingFileRef.current = null;
    }
  }, [downloadFile, fetchFileDetails, resetMarkersAndComments, audioUrl, localFiles, waveformCache, setWaveformCache, isGuest]);

  // Patch (or clear) one upload's entry in the concurrent-uploads map.
  // patch=null removes the key (that upload finished or failed). setUploads is
  // a stable setter, so this useCallback never changes identity — safe to call
  // from many concurrent handleFileUpload flows without races (each does a
  // functional update keyed by its own tempId).
  const updateUpload = useCallback((tempId, patch) => {
    setUploads((prev) => {
      if (patch === null) {
        if (!(tempId in prev)) return prev;
        const next = { ...prev };
        delete next[tempId];
        return next;
      }
      return { ...prev, [tempId]: { ...(prev[tempId] || {}), ...patch } };
    });
  }, []);

  // Handle file upload with optimistic UI. Invoked by UploadModal — payload
  // is either { mode: 'single', file } or { mode: 'dual', expertFile,
  // clientFile }. Keeps the historical fallback where the function is
  // called with a raw File (legacy path, no metadata).
  //
  // CONCURRENCY: each invocation is fully self-contained — its own tempId, its
  // own optimistic row, its own entry in the `uploads` map, its own poll loop.
  // Nothing here is gated on a global "uploading" flag anymore, so the user can
  // fire this many times in a row and all uploads run in parallel. The backend
  // caps how many actually process at once (HEAVY_PROCESSING_SEM), so extra
  // uploads simply queue server-side (their row shows stage="queued").
  const handleFileUpload = useCallback(async (payload, metadata = null) => {
    // Back-compat: direct File argument (shouldn't happen in new flow, kept
    // for safety during the transition).
    if (payload instanceof File) {
      payload = { mode: 'single', file: payload };
    }
    const isDual = payload?.mode === 'dual';
    const primary = isDual ? payload.expertFile : payload?.file;
    if (!primary) return;
    if (isDual && (!payload.expertFile || !payload.clientFile)) return;

    // Both tracks must be MP3 (UploadModal already validates, belt-and-braces).
    const checkMp3 = (f) => f && f.name.toLowerCase().endsWith('.mp3');
    if (!checkMp3(primary) || (isDual && !checkMp3(payload.clientFile))) {
      alert('Только MP3 файлы');
      return;
    }
    setUploadModalOpen(false);

    const tempId = `temp-${Date.now()}`;
    const optimisticTitle = metadata?.title || primary.name.replace('.mp3', '');
    const totalSize = isDual ? payload.expertFile.size + payload.clientFile.size : primary.size;

    const optimisticFile = {
      id: tempId,
      title: optimisticTitle,
      original_filename: primary.name,
      user_name: user?.name,
      user_id: user?.id,
      upload_date: new Date().toISOString(),
      status: 'uploading',
      file_size: totalSize,
      duration: 0,
      session_type: metadata?.session_type || null,
      client_name: metadata?.client_name || null,
      session_date: metadata?.session_date || null,
      dual_track: isDual,
    };

    addOptimisticFile(optimisticFile);

    // Register this upload in the concurrent map (its own row from here on).
    // Tracked by tempId during the byte-send phase; after the server returns a
    // real id we transfer tracking to realId (see below) and drop the temp row.
    updateUpload(tempId, { progress: 0, stage: null });

    let realId = null;
    let transitioned = false; // true once temp row swapped for the real server row
    try {
      // Phase 1: send bytes. axios onUploadProgress fires on client→server
      // byte progress. Response returns 202 as soon as the server has the
      // full file(s); heavy work (mix/compress/peaks/S3) happens afterward
      // in a background task.
      const onProgress = (e) => {
        if (e?.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          updateUpload(tempId, { progress: pct });
        }
      };
      const uploadedFile = isDual
        ? await uploadDualFile(payload.expertFile, payload.clientFile, onProgress, metadata)
        : await uploadFile(primary, onProgress, metadata);

      realId = uploadedFile.id;

      // === Transition: temp row → real server row ===
      // The 202 created a real placeholder row (realId), and uploadFile already
      // refreshed the list. If we kept our client-only temp row too, the file
      // would appear TWICE (optimistic "Загрузка" + server "Обработка"). So we
      // move progress/stage tracking onto realId and remove the temp row.
      updateUpload(realId, { progress: 100, stage: 'queued' });
      removeOptimisticFile(tempId);
      updateUpload(tempId, null);
      transitioned = true;

      // Belt-and-braces: make sure the real placeholder row is present (also
      // surfaces it for the dual path). removeOptimisticFile already dropped the
      // temp row from the re-merge set, so this won't re-introduce the dup.
      await fetchFiles();

      // Phase 2: poll /api/files/{id} every 3s while server finishes
      // compression → peaks → S3. We update THIS upload's stage label (keyed on
      // realId) from `processing_stage`. Each concurrent upload polls its own
      // realId independently.
      const POLL_INTERVAL = 3000;
      const POLL_TIMEOUT = 10 * 60 * 1000; // 10 min safety
      const startedAt = Date.now();
      let readyFile = null;
      while (Date.now() - startedAt < POLL_TIMEOUT) {
        let details;
        try {
          details = await fetchFileDetails(realId);
        } catch (e) {
          // Transient network error — keep polling
          await new Promise((r) => setTimeout(r, POLL_INTERVAL));
          continue;
        }
        if (!details) break;
        updateUpload(realId, { stage: details.processing_stage || null });
        if (details.status === 'ready') {
          readyFile = details;
          break;
        }
        if (details.status === 'error') {
          throw new Error(details.error_message || 'Ошибка обработки на сервере');
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
      }

      if (readyFile) {
        await fetchFiles(); // realId row now shows status=ready
        // Instant playback shortcut: for single-track mode, reuse the local
        // File blob (no server download). For dual mode, the playable file
        // is the server-side mix — pull it fresh from /stream.
        //
        // With concurrent uploads, DON'T yank focus away from a file the user
        // is actively viewing. Only auto-open if the user is sitting on nothing
        // or on this file (its tempId before the swap, or its realId).
        const viewing = currentFileRef.current;
        if (!viewing || viewing.id === tempId || viewing.id === realId) {
          loadAudioFile(readyFile, isDual ? null : primary);
        }
      } else {
        // Processing still in flight after 10 min — the real row stays in the
        // list (the background processing-status poller will eventually flip it
        // to ready); we just stop tracking it here.
        alert('Обработка заняла слишком долго, проверь файл в списке позже.');
      }
    } catch (error) {
      // If we errored BEFORE the temp→real swap, drop the temp row. After the
      // swap the real row exists server-side; the background poller / list
      // refresh will reflect its true status, so we leave it.
      if (!transitioned) removeOptimisticFile(tempId);
      console.error('Error uploading:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Ошибка загрузки файла';
      alert(`Не удалось загрузить файл: ${msg}`);
    } finally {
      // Clear this upload's progress entry(ies) regardless of outcome.
      updateUpload(tempId, null);
      if (realId) updateUpload(realId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFile, uploadDualFile, loadAudioFile, user, addOptimisticFile, removeOptimisticFile, fetchFileDetails, fetchFiles, updateUpload]);

  // Handle file delete
  const handleDeleteFile = useCallback(async (fileId) => {
    if (!window.confirm('Удалить файл?')) return;
    
    try {
      await deleteFile(fileId);
      if (currentFileRef.current?.id === fileId) {
        setCurrentFile(null);
        setAudioUrl(null);
        resetMarkersAndComments();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  }, [deleteFile, resetMarkersAndComments]);

  // Закрытие окна привязки. С сообщением — значит привязку меняли: подтягиваем
  // свежие поля в открытую карточку, иначе иконка и подпись остались бы от
  // прежнего состояния (список-то обновился, а currentFile — отдельный слепок).
  const handleCloseLinkModal = useCallback(async (message) => {
    setLinkModalOpen(false);
    if (!message) return;
    setLinkNotice(message);
    try {
      const fresh = await fetchFileDetails(currentFile.id);
      if (fresh) {
        setCurrentFile(prev => (prev?.id === fresh.id ? { ...prev, ...fresh } : prev));
      }
    } catch (error) {
      console.error('Не удалось обновить карточку после привязки:', error);
    }
  }, [currentFile, fetchFileDetails]);

  useEffect(() => {
    if (!linkNotice) return;
    const timer = setTimeout(() => setLinkNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [linkNotice]);

  // Edit title handlers
  const handleStartEditTitle = useCallback(() => {
    setEditTitleValue(currentFile?.title || currentFile?.original_filename?.replace('.mp3', '') || '');
    setEditingTitle(true);
  }, [currentFile]);

  const handleSaveTitle = useCallback(async () => {
    if (!editTitleValue.trim() || !currentFile) return;
    try {
      await updateFileTitle(currentFile.id, editTitleValue.trim());
      setCurrentFile(prev => ({ ...prev, title: editTitleValue.trim() }));
      setEditingTitle(false);
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Ошибка обновления названия');
    }
  }, [editTitleValue, currentFile, updateFileTitle]);

  // Create share link for current file
  const handleCreateShareLink = useCallback(async () => {
    if (!currentFile) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders().headers },
        body: JSON.stringify({ file_id: currentFile.id, ttl: SHARE_TTL_DEFAULT })
      });
      if (!response.ok) throw new Error(`share failed: ${response.status}`);
      const data = await response.json();
      // Use the OG-landing endpoint — when the link is dropped into Telegram
      // (or any messenger that scrapes Open Graph), the bot fetches THIS URL
      // and reads og:title / og:description from server-rendered HTML, so the
      // chat preview shows the file's title (e.g. "ПК - Имя | DD.MM.YYYY")
      // instead of the generic site name. Real users hitting it get bounced
      // into the SPA at /?share=<token> via meta-refresh + JS redirect.
      // Backwards-compatible: pasting an old `/?share=...` URL still works.
      const url = `${window.location.origin}/api/share/${data.token}/og`;
      setShareModalUrl(url);
      setShareToken(data.token);
      setShareTtl(data.ttl || SHARE_TTL_DEFAULT);
      setShareExpiresAt(data.expires_at);
      setShareCopied(false);
    } catch (error) {
      console.error('Error creating share link:', error);
      alert('Ошибка создания ссылки');
    }
  }, [currentFile, getAuthHeaders]);

  // Switch the link's lifetime. The URL itself never changes — the backend
  // re-stamps expires_at on the same token — so a link copied before the
  // switch stays valid with the new duration.
  const handleChangeShareTtl = useCallback(async (ttl) => {
    if (!shareToken || ttl === shareTtl) return;
    const prevTtl = shareTtl;
    setShareTtl(ttl);
    setShareTtlPending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/share/${shareToken}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders().headers },
        body: JSON.stringify({ ttl })
      });
      if (!response.ok) throw new Error(`ttl update failed: ${response.status}`);
      const data = await response.json();
      setShareExpiresAt(data.expires_at);
    } catch (error) {
      console.error('Error updating share link lifetime:', error);
      setShareTtl(prevTtl);
      alert('Не удалось изменить срок действия ссылки');
    } finally {
      setShareTtlPending(false);
    }
  }, [shareToken, shareTtl, getAuthHeaders]);

  const handleCloseShareModal = useCallback(() => {
    setShareModalUrl(null);
    setShareToken(null);
    setShareExpiresAt(null);
    setShareTtl(SHARE_TTL_DEFAULT);
    setShareCopied(false);
  }, []);

  const handleCopyShareUrl = useCallback(() => {
    if (!shareModalUrl) return;
    navigator.clipboard.writeText(shareModalUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  }, [shareModalUrl]);

  // Guest login
  const handleGuestLogin = useCallback(async (e) => {
    e.preventDefault();
    setGuestLoginError('');
    const result = await loginUser(guestLoginForm.login, guestLoginForm.password);
    if (result.success) {
      setGuestLoginModalOpen(false);
      window.location.reload();
    } else {
      setGuestLoginError(result.error);
    }
  }, [guestLoginForm, loginUser]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (wavesurfer) {
      wavesurfer.playPause();
    }
  }, [wavesurfer]);

  // Add comment
  const handleAddComment = useCallback(async () => {
    if (!commentText.trim() || !currentFileRef.current) return;

    try {
      await createMarkerWithComment(currentFileRef.current.id, currentTime, commentText);
      setCommentText('');
      setCommentsExpanded(true);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Ошибка добавления комментария');
    }
  }, [commentText, currentTime, createMarkerWithComment]);

  // Seek to timestamp and optionally start playing
  const seekToTime = useCallback((timestamp, autoPlay = false) => {
    if (wavesurfer && duration > 0 && timestamp >= 0 && timestamp <= duration) {
      wavesurfer.seekTo(timestamp / duration);
      if (autoPlay && !isPlaying) {
        wavesurfer.play();
      }
    }
  }, [wavesurfer, duration, isPlaying]);

  // Utility functions
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getInitials = useCallback((name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  }, []);

  const getAvatarColor = useCallback((name) => {
    const colors = [
      'from-orange-400 to-pink-500',
      'from-blue-400 to-purple-500',
      'from-green-400 to-teal-500',
      'from-yellow-400 to-orange-500',
      'from-pink-400 to-red-500',
      'from-indigo-400 to-blue-500'
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  }, []);

  // Memoized total comments count
  const totalComments = useMemo(() => {
    return markers.reduce((sum, m) => sum + (comments[m.id]?.length || 0), 0);
  }, [markers, comments]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-950 py-3 px-6 md:px-6 px-3 border-b border-gray-800">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Logo - hide music note on mobile, reduce size by 20% on mobile */}
            <h1 className="font-bold text-orange-500 md:text-xl text-base">
              <span className="hidden md:inline">🎵 </span>GipnoWave
            </h1>
            {/* Upload button — hidden for guests. Opens UploadModal with
                session metadata fields instead of the bare native file picker. */}
            {!isGuest && (
              <button
                onClick={() => setUploadModalOpen(true)}
                className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 md:px-4 px-2.5 py-2 rounded-full text-sm font-medium transition-colors"
                data-testid="upload-button"
              >
                {/* No longer disabled while an upload is in flight — the user
                    can queue more. A count hint shows how many are processing. */}
                <Upload size={16} />
                <span className="hidden md:inline">{uploadingCount > 0 ? `Upload (${uploadingCount})` : 'Upload'}</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            {isGuest ? (
              <>
                <span className="text-sm text-gray-400 hidden md:block">Гость</span>
                <button
                  onClick={() => setGuestLoginModalOpen(true)}
                  className="flex items-center space-x-1 bg-orange-600 hover:bg-orange-500 px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                >
                  <LogOut size={14} />
                  <span>Войти</span>
                </button>
              </>
            ) : (
              <>
                {/* User avatar/name - show as circle with initials on mobile, full name on desktop */}
                <div className="md:hidden">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(user?.name)} flex items-center justify-center text-white text-xs font-bold`}>
                    {getInitials(user?.name)}
                  </div>
                </div>
                <span className="hidden md:block text-sm text-gray-400">{user?.name}</span>
                {/* Logout button - hide text on mobile */}
                <button
                  onClick={logout}
                  className="flex items-center space-x-1 text-gray-400 hover:text-white text-sm transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut size={16} />
                  <span className="hidden md:inline">Выход</span>
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto md:px-6 px-2 md:py-6 py-4">
        {/* Список треков живёт рядом с плеером, а не внутри него: человеку,
            у которого нет доступа ни к одной записи, открывать нечего, но
            заблюренный список он всё равно должен видеть. Раньше здесь стояло
            просто `currentFile ?`, и такой человек попадал на экран «загрузите
            свой первый трек». */}
        {(currentFile || files.length > 0) ? (
          <>
            {/* Пришли по ссылке на конкретную запись, а открыть её нечем.
                Молча показывать чужой список — значит оставить человека в
                недоумении, поэтому объясняем прямо. */}
            {requestedProblem && !currentFile && (
              <div className="bg-gray-800 md:rounded-xl rounded-lg md:p-6 p-4 md:mb-6 mb-4 text-center" data-testid="requested-problem">
                <Lock size={28} className="mx-auto mb-3 text-gray-500" />
                <h2 className="text-lg font-semibold text-white mb-1">
                  {requestedProblem === 'locked' ? 'Эта запись вам не открыта' : 'Запись не найдена'}
                </h2>
                <p className="text-sm text-gray-400">
                  {requestedProblem === 'locked'
                    ? 'Возможно, вы вошли не тем аккаунтом, которому её открыли.'
                    : 'Ссылка устарела или запись удалили.'}
                </p>
              </div>
            )}

            {currentFile && (<>
            {/* Player Section */}
            <div className="bg-gray-800 md:rounded-xl rounded-lg md:p-6 p-3 md:mb-6 mb-4">
              {/* Track Header */}
              <div className="flex items-center space-x-4 mb-4">
                <button
                  onClick={togglePlayPause}
                  disabled={!isReady}
                  className="w-14 h-14 rounded-full bg-orange-600 hover:bg-orange-500 flex items-center justify-center text-white disabled:opacity-50 shadow-lg transition-all"
                  data-testid="play-pause-button"
                >
                  {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-1" />}
                </button>
                
                <div className="flex-1 min-w-0">
                  {editingTitle ? (
                    <div className="flex items-center space-x-2">
                      <input
                        autoFocus
                        type="text"
                        value={editTitleValue}
                        onChange={e => setEditTitleValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                        className="flex-1 bg-gray-700 text-white text-lg font-semibold px-2 py-1 rounded outline-none border border-orange-500"
                      />
                      <button onClick={handleSaveTitle} className="text-orange-500 hover:text-orange-400 text-sm font-medium">Сохранить</button>
                      <button onClick={() => setEditingTitle(false)} className="text-gray-500 hover:text-gray-300 text-sm">Отмена</button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 group">
                      <h2 className="text-xl font-semibold text-white truncate">
                        {currentFile.title || (currentFile.original_filename || 'Unknown').replace('.mp3', '')}
                      </h2>
                      {(isAdmin || currentFile.user_id === user?.id) && (
                        <button
                          onClick={handleStartEditTitle}
                          className="text-gray-600 hover:text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                      )}
                    </div>
                  )}
                  <p className="text-sm text-gray-400">
                    {currentFile.user_name} · {new Date(currentFile.upload_date).toLocaleDateString('ru-RU')}
                    {isAdmin && currentFile.client_profile_id && (
                      <span className="text-green-600"> · привязана к {currentFile.client_profile_name || 'клиенту'}</span>
                    )}
                  </p>
                  {linkNotice && (
                    <p className="text-xs text-green-500 mt-1" data-testid="link-notice">{linkNotice}</p>
                  )}
                </div>

                {/* Привязка записи к человеку из гипнозио — только админу.
                    Это решение о том, кто получит доступ к чужой проработке,
                    поэтому и кнопка, и сам эндпоинт закрыты одной ролью. */}
                {!isGuest && isAdmin && (
                  <button
                    onClick={() => setLinkModalOpen(true)}
                    className={`transition-colors ${
                      currentFile.client_profile_id
                        ? 'text-green-500 hover:text-green-400'
                        : 'text-gray-500 hover:text-orange-400'
                    }`}
                    title={currentFile.client_profile_id
                      ? `Привязана к ${currentFile.client_profile_name || 'клиенту'} — изменить`
                      : 'Привязать к клиенту'}
                    data-testid="link-client-button"
                  >
                    <Link2 size={20} />
                  </button>
                )}

                {/* Share button — visible to authenticated users only */}
                {!isGuest && (
                  <button
                    onClick={handleCreateShareLink}
                    className="text-gray-500 hover:text-orange-400 transition-colors"
                    title="Поделиться треком"
                    data-testid="share-button"
                  >
                    <Share2 size={20} />
                  </button>
                )}
                {/* Transcript icon — admins see all states (start / running /
                    ready), guests see only the "ready" toggle so they can read
                    the transcript that was prepared for them but not trigger
                    transcription themselves. The pending/error states are
                    admin-internal noise; guests just see no icon if the
                    transcript isn't ready yet. */}
                {(() => {
                  const st = currentFile.transcript_status || 'none';
                  if (st === 'ready') {
                    return (
                      <button
                        onClick={() => setTranscriptExpanded(v => !v)}
                        className="text-orange-400 hover:text-orange-300 transition-colors"
                        title={transcriptExpanded ? 'Скрыть транскрипт' : 'Показать транскрипт'}
                        data-testid="transcript-toggle-button"
                      >
                        <FileText size={20} />
                      </button>
                    );
                  }
                  if (!isAdmin) return null;  // Guests/regular users: no other states
                  if (st === 'pending' || st === 'running') {
                    return (
                      <div
                        className="text-orange-400 flex items-center"
                        title="Транскрипция выполняется…"
                        data-testid="transcribe-loading"
                      >
                        <Loader2 size={20} className="animate-spin" />
                      </div>
                    );
                  }
                  // 'none' or 'error' — admin can trigger / retry
                  return (
                    <button
                      onClick={handleStartTranscription}
                      className={`text-gray-500 hover:text-orange-400 transition-colors ${st === 'error' ? 'text-red-500 hover:text-red-400' : ''}`}
                      title={st === 'error' ? `Ошибка: ${currentFile.transcript_error || 'неизвестно'}. Попробовать ещё раз.` : 'Транскрибировать'}
                      data-testid="transcribe-button"
                    >
                      <FileText size={20} />
                    </button>
                  );
                })()}
                {(isAdmin || currentFile.user_id === user?.id) && (
                  <button
                    onClick={() => handleDeleteFile(currentFile.id)}
                    className="text-gray-500 hover:text-red-500 transition-colors"
                    data-testid="delete-file-button"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>

              {/* Waveform Container */}
              <div
                ref={waveformWrapperRef}
                className="relative bg-gray-900 md:rounded-lg overflow-x-auto overflow-y-hidden mb-2"
                style={{ height: '148px' }}
              >
                {/* Loader — visible while waveform is initializing OR audio URL is not ready yet */}
                {(isLoadingWaveform || !audioUrl) && (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-gray-900/95" data-testid="waveform-loading">
                    <Loader2 size={40} className="text-orange-500 animate-spin mb-3" />
                    <div className="text-sm text-gray-300 font-medium">Загружаю аудио…</div>
                    <div className="text-xs text-gray-500 mt-1">Первое открытие может занять до 30 сек</div>
                  </div>
                )}

                {/* Current time label — top-left so it doesn't overlap the timeline ruler */}
                <div className="absolute top-1 left-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded z-20 pointer-events-none">
                  {formatTime(currentTime)}
                </div>



                {/* Waveform + Timeline (timeline rendered by WaveSurfer plugin below waveform) */}
                <div
                  ref={containerRef}
                  className="h-full cursor-pointer relative"
                  style={{ minWidth: '100%' }}
                  data-testid="waveform"
                >
                  {/* Markers removed from waveform - comments are shown in list below */}
                </div>
              </div>

              {/* Transcript panel — visible to everyone with a "ready" transcript
                  (admins, regular users, and guests opening a share link). The
                  panel is read-only for non-admins: speaker-label edit and the
                  copy/download icons are gated below by `isAdmin`. Mutations
                  on the backend require JWT regardless, so even a malicious
                  guest hitting the API directly can't change anything. */}
              {transcriptExpanded && currentFile.transcript_status === 'ready' && currentFile.transcript && (() => {
                const hasKaraoke = Array.isArray(currentFile.transcript_words) && currentFile.transcript_words.length > 0;
                // Translation tab is shown ONLY for foreign-language sessions
                // that successfully completed the optional translation step.
                // For Russian / multi / legacy / unset sources this stays
                // false → no extra button, no behavior change vs. before.
                const hasTranslation = Array.isArray(currentFile.transcript_paragraphs_ru) && currentFile.transcript_paragraphs_ru.length > 0;
                // Resolve the effective view mode. The user's explicit choice
                // (`transcriptViewMode`) wins; we only override when their
                // choice is unavailable on this file (e.g. they were on
                // 'translation' and switched to a Russian file). Default for
                // non-Russian files with translation present is 'translation'.
                let effectiveMode = transcriptViewMode;
                if (effectiveMode === 'translation' && !hasTranslation) effectiveMode = hasKaraoke ? 'karaoke' : 'text';
                if (effectiveMode === 'karaoke' && !hasKaraoke) effectiveMode = hasTranslation ? 'translation' : 'text';
                return (
                  <div className="mt-3 bg-gray-800 rounded-lg p-3 md:p-4" data-testid="transcript-panel">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-400">
                        <FileText size={16} />
                        <span>Транскрипт</span>
                        <span className="text-xs text-gray-500">
                          · {currentFile.transcript.length.toLocaleString('ru-RU')} симв.
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                        {(hasKaraoke || hasTranslation) && (
                          <div className="flex bg-gray-900 rounded p-0.5 mr-2" data-testid="transcript-mode-toggle">
                            {hasKaraoke && (
                              <button
                                onClick={() => setTranscriptViewMode('karaoke')}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  effectiveMode === 'karaoke' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'
                                }`}
                                data-testid="mode-karaoke"
                              >
                                Синхрон
                              </button>
                            )}
                            {hasTranslation && (
                              <button
                                onClick={() => setTranscriptViewMode('translation')}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  effectiveMode === 'translation' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'
                                }`}
                                data-testid="mode-translation"
                                title="Перевод на русский — караоке по предложениям"
                              >
                                Перевод
                              </button>
                            )}
                            <button
                              onClick={() => setTranscriptViewMode('text')}
                              className={`px-2 py-1 text-xs rounded transition-colors ${
                                effectiveMode === 'text' ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'
                              }`}
                              data-testid="mode-text"
                            >
                              Текст
                            </button>
                          </div>
                        )}
                        <button
                          onClick={handleCopyTranscript}
                          className="text-gray-500 hover:text-orange-400 transition-colors p-1"
                          title="Скопировать текст"
                          data-testid="transcript-copy-btn"
                        >
                          {transcriptCopied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                        <button
                          onClick={handleDownloadTranscript}
                          className="text-gray-500 hover:text-orange-400 transition-colors p-1"
                          title="Скачать как .txt"
                          data-testid="transcript-download-btn"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          onClick={() => setTranscriptExpanded(false)}
                          className="text-gray-500 hover:text-white transition-colors p-1"
                          title="Скрыть"
                          data-testid="transcript-close-btn"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>

                    {effectiveMode === 'karaoke' && hasKaraoke ? (
                      <KaraokeView
                        mode="words"
                        words={currentFile.transcript_words}
                        paragraphs={currentFile.transcript_paragraphs}
                        currentTime={currentTime}
                        onSeek={handleSeekFromKaraoke}
                        speakerLabels={currentFile.transcript_speaker_labels || {}}
                        onSpeakerLabelsChange={isAdmin ? handleUpdateSpeakerLabels : null}
                      />
                    ) : effectiveMode === 'translation' && hasTranslation ? (
                      <KaraokeView
                        mode="sentences"
                        sentences={currentFile.transcript_paragraphs_ru}
                        currentTime={currentTime}
                        onSeek={handleSeekFromKaraoke}
                        speakerLabels={currentFile.transcript_speaker_labels || {}}
                        onSpeakerLabelsChange={isAdmin ? handleUpdateSpeakerLabels : null}
                      />
                    ) : currentFile.dual_track && currentFile.transcript_paragraphs?.length > 0 ? (
                      // Dual-track text view: render paragraphs grouped by speaker
                      // with labels. Each paragraph has a known speaker tag (set
                      // during transcription because each track was processed
                      // independently), so no guessing needed.
                      <div
                        className="text-sm text-gray-200 max-h-96 overflow-y-auto leading-relaxed space-y-4"
                        data-testid="transcript-text"
                      >
                        {currentFile.transcript_paragraphs.map((p, i) => {
                          const label = (currentFile.transcript_speaker_labels || {})[String(p.speaker)]
                            || `Спикер ${(p.speaker ?? 0) + 1}`;
                          const ts = Math.floor(p.start || 0);
                          const mm = Math.floor(ts / 60);
                          const ss = String(ts % 60).padStart(2, '0');
                          return (
                            <div key={i} data-testid={`transcript-text-paragraph-${i}`}>
                              <div className="text-xs font-semibold text-orange-400 mb-1">
                                {label} · {mm}:{ss}
                              </div>
                              <div className="whitespace-pre-wrap">{p.text}</div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className="text-sm text-gray-200 whitespace-pre-wrap max-h-96 overflow-y-auto leading-relaxed"
                        data-testid="transcript-text"
                      >
                        {currentFile.transcript}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Transcript running status — compact inline notice */}
              {isAdmin && (currentFile.transcript_status === 'pending' || currentFile.transcript_status === 'running') && (
                <div className="mt-3 bg-gray-800 rounded-lg p-3 flex items-center space-x-3 text-sm text-gray-300" data-testid="transcript-running">
                  <Loader2 size={16} className="animate-spin text-orange-400" />
                  <span>Транскрибирую... обычно занимает 1-5 минут, зависит от длины аудио.</span>
                </div>
              )}

              {/* Transcript error */}
              {isAdmin && currentFile.transcript_status === 'error' && (
                <div className="mt-3 bg-red-900/30 border border-red-700 rounded-lg p-3 flex items-start space-x-3 text-sm" data-testid="transcript-error">
                  <X size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-red-200">
                    <div className="font-medium mb-1">Ошибка транскрипции</div>
                    <div className="text-xs text-red-300 break-words">{currentFile.transcript_error || 'неизвестная ошибка'}</div>
                    <button
                      onClick={handleStartTranscription}
                      className="mt-2 px-3 py-1 bg-red-800 hover:bg-red-700 text-white text-xs rounded transition-colors"
                      data-testid="transcript-retry-btn"
                    >
                      Попробовать ещё раз
                    </button>
                  </div>
                </div>
              )}

              {/* Zoom Control Slider */}
              <div className="flex items-center space-x-3 mt-3 px-1">
                <button
                  onClick={() => handleZoomChange(Math.max(MIN_ZOOM, zoom - 0.5))}
                  disabled={zoom <= MIN_ZOOM}
                  className="p-1.5 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="zoom-out-btn"
                >
                  <ZoomOut size={16} className="text-gray-300" />
                </button>
                
                <div className="flex-1 flex items-center space-x-3">
                  {/* Current zoom value on the left */}
                  <span className="text-xs text-orange-500 font-medium w-10">x{zoom.toFixed(1)}</span>
                  <div className="flex-1 relative h-6 flex items-center">
                    {/* Track background */}
                    <div className="absolute inset-y-2 left-0 right-0 bg-gray-700 rounded-full" />
                    {/* Active track */}
                    <div 
                      className="absolute inset-y-2 left-0 bg-gradient-to-r from-orange-600 to-orange-500 rounded-full"
                      style={{ width: `${((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100}%` }}
                    />
                    {/* Slider input */}
                    <input
                      type="range"
                      min={MIN_ZOOM}
                      max={MAX_ZOOM}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => handleZoomChange(parseFloat(e.target.value))}
                      className="absolute inset-0 w-full opacity-0 cursor-pointer"
                      data-testid="zoom-slider"
                    />
                    {/* Thumb indicator */}
                    <div 
                      className="absolute w-4 h-4 bg-white rounded-full shadow-lg border-2 border-orange-500 pointer-events-none transform -translate-x-1/2"
                      style={{ left: `${((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-8">10x</span>
                </div>

                <button
                  onClick={() => handleZoomChange(Math.min(MAX_ZOOM, zoom + 0.5))}
                  disabled={zoom >= MAX_ZOOM}
                  className="p-1.5 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="zoom-in-btn"
                >
                  <ZoomIn size={16} className="text-gray-300" />
                </button>

                {/* Playback speed button */}
                <button
                  onClick={handlePlaybackRateChange}
                  className="px-3 py-1.5 rounded-full bg-gray-700 hover:bg-gray-600 text-xs text-gray-300 font-medium transition-colors min-w-[48px]"
                  data-testid="playback-rate-btn"
                >
                  x{playbackRate}
                </button>
              </div>

              {/* Keyboard hint */}
              <div className="flex items-center justify-center mt-2 text-xs text-gray-600">
                <span><kbd className="px-1.5 py-0.5 bg-gray-700 rounded text-gray-400">Space</kbd> — воспроизведение/пауза</span>
              </div>

              {/* Comment Input */}
              {isGuest ? (
                <div className="mt-4">
                  <button
                    onClick={() => setGuestLoginModalOpen(true)}
                    className="w-full flex items-center justify-center space-x-2 py-2.5 bg-gray-700/60 hover:bg-gray-700 border border-gray-600 rounded-full text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <MessageCircle size={16} />
                    <span>Войдите, чтобы оставить комментарий</span>
                  </button>
                </div>
              ) : (
                <div className="mt-4 flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(user?.name)} flex items-center justify-center text-white text-sm font-bold`}>
                    {getInitials(user?.name)}
                  </div>

                  <div className="flex-1 flex items-center bg-gray-700 rounded-full overflow-hidden">
                    <input
                      type="text"
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                      placeholder={`Добавить комментарий на ${formatTime(currentTime)}...`}
                      disabled={!isReady}
                      className="flex-1 px-4 py-2 bg-transparent text-white text-sm outline-none placeholder-gray-500"
                      data-testid="comment-input"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || !isReady}
                      className="px-4 py-2 text-orange-500 hover:text-orange-400 disabled:text-gray-600 transition-colors"
                      data-testid="add-comment-button"
                    >
                      <MessageCircle size={20} />
                    </button>
                  </div>
                </div>
              )}

              {/* Collapsible Comments Section */}
              <div className="mt-4">
                <button
                  onClick={() => setCommentsExpanded(!commentsExpanded)}
                  className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors w-full justify-between py-2 border-t border-gray-700"
                  data-testid="toggle-comments"
                >
                  <div className="flex items-center space-x-2">
                    <MessageCircle size={18} />
                    <span className="text-sm">{totalComments} комментариев</span>
                  </div>
                  {commentsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {/* Comments List */}
                <div 
                  className={`overflow-hidden transition-all duration-300 ${commentsExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className="space-y-3 pt-3 max-h-80 overflow-y-auto">
                    {markers.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">Комментариев пока нет</p>
                    ) : (
                      markers.map(marker => {
                        const markerComments = comments[marker.id] || [];
                        return markerComments.map(comment => (
                          <CommentItem
                            key={comment.id}
                            comment={comment}
                            marker={marker}
                            getInitials={getInitials}
                            getAvatarColor={getAvatarColor}
                            formatTime={formatTime}
                            seekToTime={seekToTime}
                            deleteComment={deleteComment}
                            currentUserId={user?.id}
                            isAdmin={isAdmin}
                          />
                        ));
                      })
                    )}
                  </div>
                </div>
              </div>
            </div>
            </>)}

            {/* File List Section — hidden for guests */}
            {!isGuest && (
              <div className="bg-gray-800 md:rounded-xl rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-400">Все треки</h3>
                  {filesLoading && (
                    <Loader2 size={14} className="animate-spin text-orange-400" data-testid="files-list-loading" />
                  )}
                </div>
                {filesLoading && files.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-500" data-testid="files-list-loading-msg">
                    <Loader2 size={20} className="mx-auto mb-2 text-orange-400 animate-spin" />
                    Загружаю список треков…
                  </div>
                ) : (<div className="divide-y divide-gray-700">
                  {files.map((file, index) => {
                    const isActive = currentFile?.id === file.id;
                    const fileStatus = file.status || 'ready';
                    const isProcessing = fileStatus !== 'ready';
                    // Запись, к которой у вошедшего нет доступа. Сервер прислал
                    // одну лишь заглушку: под размытием нет ни настоящего
                    // названия, ни имени клиента — их просто нет в ответе.
                    const isLocked = !!file.locked;

                    return (
                      <div
                        key={file.id}
                        onClick={() => {
                          if (isLocked) { setLockedHintId(file.id); return; }
                          if (!isProcessing) loadAudioFile(file);
                        }}
                        title={isLocked ? 'Запись доступна тем, кто проходит эту проработку' : undefined}
                        className={`flex items-center space-x-4 px-4 py-3 transition-colors ${
                          isLocked
                            ? 'cursor-not-allowed hover:bg-gray-700/20'
                            : isProcessing
                              ? 'opacity-60 cursor-wait'
                              : `cursor-pointer ${isActive ? 'bg-gray-700/70' : 'hover:bg-gray-700/30'}`
                        }`}
                        data-testid={`file-item-${file.id}`}
                      >
                        {/* Track Number */}
                        <span className={`w-6 text-sm ${isActive ? 'text-orange-500' : 'text-gray-500'}`}>
                          {index + 1}
                        </span>

                        {/* Avatar — у закрытой записи вместо автора замок */}
                        {isLocked ? (
                          <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-500 flex-shrink-0">
                            <Lock size={16} />
                          </div>
                        ) : (
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(file.user_name || user?.name)} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                            {getInitials(file.user_name || user?.name)}
                          </div>
                        )}

                        {/* Track Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            {isLocked ? (
                              <>
                                <span
                                  className="text-sm font-medium text-gray-300 blur-[5px] select-none truncate"
                                  aria-hidden="true"
                                >
                                  {file.title || 'Запись'}
                                </span>
                                <span className="sr-only">Запись без доступа</span>
                              </>
                            ) : (
                              <>
                                <span className={`text-sm ${isActive ? 'text-orange-500' : 'text-gray-400'}`}>
                                  {file.user_name || user?.name}
                                </span>
                                <span className="text-gray-600">·</span>
                                <span className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-gray-200'}`}>
                                  {file.title || (file.original_filename || 'Unknown').replace('.mp3', '')}
                                </span>

                                {/* Status Badge */}
                                <FileStatusBadge status={fileStatus} />
                              </>
                            )}
                          </div>

                          {isLocked && lockedHintId === file.id && (
                            <p className="mt-1 text-xs text-gray-500" data-testid="locked-hint">
                              Эта запись доступна тем, кто проходит эту проработку.
                            </p>
                          )}

                          {/* Per-row upload progress — read from the concurrent
                              uploads map by this row's tempId. Multiple rows can
                              show progress simultaneously now. */}
                          {uploads[file.id] && uploads[file.id].progress !== null && (() => {
                            const { progress: rowProgress, stage: rowStage } = uploads[file.id];
                            const stageLabels = {
                              queued: 'В очереди на обработку…',
                              compressing: 'Сжимаю аудио (LAME 64k)…',
                              generating_peaks: 'Генерирую волну…',
                              mixing: 'Свожу дорожки…',
                              uploading_to_s3: 'Загружаю в хранилище…',
                            };
                            const phase2Label = stageLabels[rowStage] || 'Обрабатываю на сервере…';
                            return (
                              <div className="mt-2" data-testid="upload-progress">
                                <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                                  <span>
                                    {rowProgress < 100
                                      ? `Загружаю на сервер… ${rowProgress}%`
                                      : phase2Label}
                                  </span>
                                  {rowProgress < 100 && (
                                    <span className="font-mono text-orange-400">{rowProgress}%</span>
                                  )}
                                </div>
                                <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                                  {rowProgress < 100 ? (
                                    <div
                                      className="h-full bg-orange-500 transition-all duration-200 ease-out"
                                      style={{ width: `${rowProgress}%` }}
                                    />
                                  ) : (
                                    <div className="h-full bg-orange-500/50 animate-pulse" style={{ width: '100%' }} />
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Playing indicator */}
                        {isActive && isPlaying && !isProcessing && (
                          <div className="flex space-x-0.5">
                            <div className="w-1 h-4 bg-orange-500 animate-pulse rounded"></div>
                            <div className="w-1 h-4 bg-orange-500 animate-pulse rounded" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-1 h-4 bg-orange-500 animate-pulse rounded" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>)}
              </div>
            )}
          </>
        ) : filesLoading ? (
          /* Loading state — shown immediately after login while fetching files */
          <div className="text-center py-20" data-testid="files-loading">
            <Loader2 size={48} className="mx-auto mb-4 text-orange-500 animate-spin" />
            <h2 className="text-xl font-semibold text-white mb-2">Загружаю ваши треки…</h2>
            <p className="text-gray-400 text-sm">Секундочку, подтягиваю список с сервера</p>
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🎵</div>
            <h2 className="text-2xl font-bold text-white mb-4">Загрузите свой первый трек</h2>
            <p className="text-gray-400 mb-6">Поддерживаются MP3 файлы до 500MB</p>
            <button
              onClick={() => setUploadModalOpen(true)}
              className="px-8 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-full font-medium transition-colors"
              data-testid="upload-first-track-button"
            >
              Выбрать файл
            </button>
          </div>
        )}
      </div>

      {/* Upload modal with session metadata. NOT gated on in-flight uploads —
          the user can open it and submit another file while others process;
          each handleFileUpload call runs concurrently with its own tempId. */}
      {uploadModalOpen && (
        <UploadModal
          searchClients={searchClients}
          fetchClientSessions={fetchClientSessions}
          onClose={() => setUploadModalOpen(false)}
          onSubmit={(file, metadata) => handleFileUpload(file, metadata)}
        />
      )}

      {/* Привязка записи к человеку из гипнозио — только у админа */}
      {linkModalOpen && currentFile && isAdmin && (
        <LinkClientModal
          file={currentFile}
          searchClients={searchClients}
          updateFileAccess={updateFileAccess}
          onClose={handleCloseLinkModal}
        />
      )}

      {/* Share URL Modal */}
      {shareModalUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={handleCloseShareModal}>
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Ссылка на аудио запись</h3>
              <button onClick={handleCloseShareModal} className="text-gray-500 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {SHARE_TTL_OPTIONS.map(option => (
                <button
                  key={option.key}
                  onClick={() => handleChangeShareTtl(option.key)}
                  disabled={shareTtlPending}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${
                    shareTtl === option.key
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  data-testid={`share-ttl-${option.key}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-400 mb-3">
              {shareExpiresAt
                ? `Ссылка действительна до ${new Date(shareExpiresAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}. `
                : ''}
              Любой с этой ссылкой может прослушать запись.
            </p>
            <div className="flex items-center space-x-2 bg-gray-700 rounded-xl px-3 py-2 mb-4">
              <span className="flex-1 text-sm text-gray-300 truncate">{shareModalUrl}</span>
              <button
                onClick={handleCopyShareUrl}
                className="flex items-center space-x-1 text-orange-500 hover:text-orange-400 text-sm font-medium flex-shrink-0 transition-colors"
              >
                {shareCopied ? <Check size={16} /> : <Copy size={16} />}
                <span>{shareCopied ? 'Скопировано' : 'Копировать'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Login Modal */}
      {guestLoginModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4" onClick={() => setGuestLoginModalOpen(false)}>
          <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Войти в аккаунт</h3>
              <button onClick={() => setGuestLoginModalOpen(false)} className="text-gray-500 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleGuestLogin} className="space-y-3">
              <input
                type="text"
                placeholder="Логин"
                value={guestLoginForm.login}
                onChange={e => setGuestLoginForm(prev => ({ ...prev, login: e.target.value }))}
                className="w-full bg-gray-700 text-white px-4 py-2.5 rounded-xl outline-none border border-transparent focus:border-orange-500 text-sm"
                autoFocus
              />
              <input
                type="password"
                placeholder="Пароль"
                value={guestLoginForm.password}
                onChange={e => setGuestLoginForm(prev => ({ ...prev, password: e.target.value }))}
                className="w-full bg-gray-700 text-white px-4 py-2.5 rounded-xl outline-none border border-transparent focus:border-orange-500 text-sm"
              />
              {guestLoginError && <p className="text-red-400 text-sm">{guestLoginError}</p>}
              <button
                type="submit"
                className="w-full bg-orange-600 hover:bg-orange-500 text-white py-2.5 rounded-xl font-medium text-sm transition-colors"
              >
                Войти
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default MainPage;
